<template>
  <div class="manager-company-edit-ui">
    <VForm @submit="handleUpdate">
      <div class="manager-company-edit-ui__form">
        <div v-if="externalId" class="manager-company-edit-ui__data-row">
          <span>External id: {{ externalId }}</span>
          <CopyBtn @click="copyExternalId" />
        </div>
        <VFormFeedback :error="errors.name && errors.name[0]" class="no-margin">
          <VInput
            v-model="payload.name"
            :config="{
              ...BASE_INPUT_CONFIG,
              label: 'Name',
              readonly: isLoading,
            }"
            class="slim"
          />
        </VFormFeedback>
        <div class="manager-company-edit-ui__alias-field">
          <MultiSelectAutocomplete
            v-model="aliasValue"
            :search-performer="aliasesSearchPerformer"
            :selection="aliasesList"
            :search-min-chars="2"
            variation-class-name="slim"
            placeholder="Alias"
            search-result-field="name"
            selection-display-key="name"
            @on-selection-add="addAlias"
            @on-selection-remove="deleteAlias"
          />
          <p v-if="aliasesList.length === 0">
            <i class="fr fr-triangle-warn glyph manager-company-edit-ui__warning-icon"></i>
            No aliases set. Please set aliases if needed
          </p>
        </div>
        <ManagerCompanyEditSection title="Sync Configuration">
          <VFormFeedback :error="errors.sync_archived && errors.sync_archived[0]">
            <VCheckbox
              v-model="payload.sync_archived"
              label="Sync archived"
              :disabled="isLoading"
            />
          </VFormFeedback>
          <VFormFeedback :error="errors.write_journal && errors.write_journal[0]">
            <VCheckbox
              v-model="payload.write_journal"
              label="Write notes to Reapit journal"
              :disabled="isLoading"
            />
          </VFormFeedback>
          <div class="manager-company-edit-ui__group">
            <p class="manager-company-edit-ui__group-label">Contact Types</p>
            <p class="manager-company-edit-ui__info-text">Vendors are always synced</p>
            <div class="manager-company-edit-ui__checkbox-row">
              <VFormFeedback
                :error="errors.sync_landlords && errors.sync_landlords[0]"
                class="manager-company-edit"
              >
                <VCheckbox
                  v-model="payload.sync_landlords"
                  label="Sync landlords"
                  :disabled="isLoading"
                />
              </VFormFeedback>
              <VFormFeedback
                :error="errors.sync_applicants && errors.sync_applicants[0]"
                class="manager-company-edit"
              >
                <VCheckbox
                  v-model="payload.sync_applicants"
                  label="Sync applicants"
                  :disabled="isLoading"
                />
              </VFormFeedback>
            </div>
          </div>
          <div class="manager-company-edit-ui__group">
            <p class="manager-company-edit-ui__group-label">Marketing Consent</p>
            <p class="manager-company-edit-ui__info-text">
              Contacts with 'Yes' consent are always synced
            </p>
            <div class="manager-company-edit-ui__checkbox-row">
              <VFormFeedback
                :error="errors.excluded_marketing_consents && errors.excluded_marketing_consents[0]"
                class="manager-company-edit"
              >
                <VCheckbox
                  v-model="includeDenyConsent"
                  label="Sync 'Deny' consent"
                  :disabled="isLoading"
                />
              </VFormFeedback>
              <VFormFeedback class="manager-company-edit">
                <VCheckbox
                  v-model="includeNotAskedConsent"
                  label="Sync 'Not Set' consent"
                  :disabled="isLoading"
                />
              </VFormFeedback>
            </div>
          </div>
        </ManagerCompanyEditSection>
        <!--        Section only visible only if customer is a reapit customer-->
        <ManagerCompanyEditSection v-if="externalId" title="Information displayed to end-users">
          <VFormFeedback :error="errors.show_contact_info && errors.show_contact_info[0]">
            <VCheckbox
              v-model="payload.show_contact_info"
              label="Show contact information in Pitch Panel"
              :disabled="isLoading"
            />
          </VFormFeedback>
          <VFormFeedback :error="errors.allow_journal && errors.allow_journal[0]">
            <VCheckbox
              v-model="payload.allow_journal"
              label="Allow journal events to be retrieved from Reapit"
              :disabled="isLoading"
            />
          </VFormFeedback>
        </ManagerCompanyEditSection>
        <VBtn class="full-width slim" type="submit" :disabled="isLoading">Update</VBtn>
      </div>
    </VForm>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { TManagerCompanyErrors, TNewManagerCompany } from '~/modules/manager/types';
  import type { TSearchPerformer } from '~/misc/search/types';
  import type { TAgent } from '~/misc/agent/types';
  import type { WritableComputedRef } from 'vue';

  // Enums
  import { EMarketingConsent } from '~/modules/manager/types';

  // Config
  import { BASE_INPUT_CONFIG } from '~/modules/manager/company/edit/config';

  // Services
  import { ManagerCompanyDataClient } from '~/modules/manager/services/manager-company-data-client';
  import { ManagerAliasesSearchPerformer } from '~/modules/manager/services/aliases-search-performer';
  import { ClipboardService } from '~/misc/services/clipboard';

  // Composables
  import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';
  import { useManagerCompanyAliasesInput } from '~/modules/manager/company/composables/manager-company-aliases-input';

  // Components
  import VForm from '~/misc/v-form';
  import VFormFeedback from '~/misc/v-form-feedback';
  import VInput from '~/misc/v-input';
  import VBtn from '~/misc/v-btn';
  import VCheckbox from '~/misc/v-checkbox';
  import MultiSelectAutocomplete from '~/misc/multi-select-autocomplete/MultiSelectAutocomplete.vue';
  import ManagerCompanyEditSection from '~/modules/manager/company/edit/section/ManagerCompanyEditSection.vue';
  import CopyBtn from '~/misc/btn/copy/CopyBtn.vue';

  const props = defineProps({
    companyId: {
      type: Number as PropType<number | null>,
      default: null,
    },
    externalId: {
      type: String as PropType<string | null | undefined>,
      required: true,
    },
    payload: {
      type: Object as PropType<TNewManagerCompany>,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  });

  const managerCompanyDataClient: ManagerCompanyDataClient = new ManagerCompanyDataClient();

  const aliasesSearchPerformer: TSearchPerformer = new ManagerAliasesSearchPerformer();

  const { warning: showWarning, success } = useToast();
  const { notifySuccess } = useNotifyResource();
  const {
    inputValue: aliasValue,
    list: aliasesList,
    error: aliasError,
    setList: setAliasesList,
    addItem: addAlias,
    deleteItem: deleteAlias,
  } = useManagerCompanyAliasesInput();

  const payload: Ref<TNewManagerCompany> = ref<TNewManagerCompany>({ ...props.payload });
  const errors: Ref<TManagerCompanyErrors> = ref<TManagerCompanyErrors>({});
  const isLoading: Ref<boolean> = ref<boolean>(props.loading);

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const setErrors = (data: TManagerCompanyErrors) => {
    errors.value = data;
  };

  const updateExcludedConsents = (value: EMarketingConsent, exclude: boolean) => {
    const current: EMarketingConsent[] = [...payload.value.excluded_marketing_consents];
    const index: number = current.indexOf(value);

    if (exclude && index === -1) {
      current.push(value);
    } else if (!exclude && index !== -1) {
      current.splice(index, 1);
    }

    payload.value.excluded_marketing_consents = current;
  };

  const includeDenyConsent: WritableComputedRef<boolean> = computed({
    get: () => !payload.value.excluded_marketing_consents.includes(EMarketingConsent.DENY),
    set: (include: boolean) => updateExcludedConsents(EMarketingConsent.DENY, !include),
  });

  const includeNotAskedConsent: WritableComputedRef<boolean> = computed({
    get: () => !payload.value.excluded_marketing_consents.includes(EMarketingConsent.NOT_ASKED),
    set: (include: boolean) => updateExcludedConsents(EMarketingConsent.NOT_ASKED, !include),
  });

  const handleUpdate = async () => {
    if (isLoading.value) return;

    if (props.companyId === null) {
      throw new Error('Company ID is not provided');
    }

    setIsLoading(true);

    await managerCompanyDataClient
      .request('update', props.companyId, {
        ...payload.value,
        aliases: aliasesList.value.map((item: TAgent) => item.id),
      })
      .then(notifySuccess)
      .catch((e) => {
        if (e?.response?.data?.errors) {
          setErrors(e.response.data.errors);
        } else {
          throw new Error(e?.response?.data?.message || undefined);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const copyExternalId = () => {
    if (!props.externalId) return;

    ClipboardService.copy(props.externalId);
    success({ message: `${props.externalId} copied to clipboard` });
  };

  watch(
    () => props.payload,
    (data: TNewManagerCompany) => {
      payload.value = {
        ...data,
        excluded_marketing_consents: data.excluded_marketing_consents ?? [],
      };

      setAliasesList(data.aliases);
    },
    { immediate: true, deep: true }
  );

  watch(
    () => props.loading,
    (state: boolean) => {
      setIsLoading(state);
    },
    { immediate: true }
  );

  watch(aliasError, (error: string) => {
    if (!error) return;

    showWarning({ message: error });
  });
</script>
